import { DialogComponent } from '@theme/dialog';

const DISMISSED_STORAGE_KEY = 'ax:localization-popup:dismissed-at';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Popup de localisation native (remplace la modale de l'app Orbe).
 *
 * Détecte le pays du visiteur via l'endpoint natif Shopify
 * `browsing_context_suggestions.json` (first-party, appelé après idle) et
 * propose le bon marché/langue via le formulaire de localization Shopify.
 *
 * Les boutons portant `data-localization-trigger` (header, drawer, footer)
 * ouvrent la popup manuellement.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLParagraphElement} [text] - The popup description text.
 * @property {HTMLSelectElement} [countrySelect] - The country select.
 * @property {HTMLSelectElement} [languageSelect] - The language select.
 *
 * @extends DialogComponent
 */
class LocalizationPopup extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('dialog:close', this.#saveDismissal);
    this.querySelector('form')?.addEventListener('submit', this.#saveDismissal);

    this.#scheduleSuggestionCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    this.#removeActivityListeners();
  }

  /**
   * Opens the popup when a `[data-localization-trigger]` button is clicked.
   *
   * @param {MouseEvent} event - The click event.
   */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-localization-trigger]');
    if (!trigger) return;

    event.preventDefault();
    this.showDialog();
  };

  // Mêmes events que le bloc `launchevent` d'observer-CFH : un test lab
  // (Lighthouse/PSI, WebPageTest) sans interaction ne déclenche jamais la popup
  #activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'keypress', 'touchmove'];

  #scheduleSuggestionCheck() {
    if (this.dataset.autoOpen !== 'true') return;
    if (window.Shopify?.designMode) return;
    if (/Lighthouse|PTST|GTmetrix/i.test(navigator.userAgent)) return;
    if (this.#isDismissed()) return;

    for (const type of this.#activityEvents) {
      window.addEventListener(type, this.#onFirstInteraction, { passive: true });
    }
  }

  #onFirstInteraction = () => {
    this.#removeActivityListeners();
    this.#checkSuggestion();
  };

  #removeActivityListeners() {
    for (const type of this.#activityEvents) {
      window.removeEventListener(type, this.#onFirstInteraction);
    }
  }

  async #checkSuggestion() {
    const currentCountry = window.Shopify?.country;
    if (!currentCountry) return;

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const url = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
      url.searchParams.set('country[enabled]', 'true');
      url.searchParams.set('country[exclude]', currentCountry);
      url.searchParams.set('language[enabled]', 'true');

      const response = await fetch(url);
      if (!response.ok) return;

      const data = await response.json();
      const suggestion = data.suggestions?.[0];
      const suggestedCountry = suggestion?.parts?.country;
      const detectedCountry = data.detected_values?.country;

      if (suggestedCountry && suggestedCountry.handle !== currentCountry) {
        this.#applySuggestion(suggestedCountry.handle, suggestion.parts?.language?.handle);
        this.showDialog();
        return;
      }

      // Pays détecté mais absent des marchés : message « non desservi »
      if (detectedCountry && detectedCountry.handle !== currentCountry && !this.#hasCountryOption(detectedCountry.handle)) {
        this.#showUnavailableMessage(detectedCountry.name);
        this.showDialog();
      }
    } catch {
      // Détection best-effort : ne jamais impacter la navigation
    }
  }

  /**
   * Preselects the suggested country and language in the localization form.
   *
   * @param {string} [countryHandle] - The suggested country ISO code.
   * @param {string} [languageHandle] - The suggested language ISO code.
   */
  #applySuggestion(countryHandle, languageHandle) {
    const { countrySelect, languageSelect } = this.refs;

    if (countryHandle && this.#hasCountryOption(countryHandle)) {
      countrySelect.value = countryHandle;
    }

    if (languageHandle && languageSelect?.querySelector(`option[value="${CSS.escape(languageHandle)}"]`)) {
      languageSelect.value = languageHandle;
    }
  }

  /**
   * @param {string} countryHandle - The country ISO code.
   * @returns {boolean} Whether the country is available in the form.
   */
  #hasCountryOption(countryHandle) {
    return Boolean(this.refs.countrySelect?.querySelector(`option[value="${CSS.escape(countryHandle)}"]`));
  }

  /**
   * @param {string} countryName - The detected country name.
   */
  #showUnavailableMessage(countryName) {
    const { text } = this.refs;
    const template = this.dataset.unavailableText;
    if (!text || !template) return;

    text.textContent = template.replace('[country]', countryName);
  }

  #isDismissed() {
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISSED_STORAGE_KEY));
      return Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS;
    } catch {
      return false;
    }
  }

  #saveDismissal = () => {
    try {
      localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()));
    } catch {
      // localStorage indisponible (navigation privée) : la popup pourra se réafficher
    }
  };
}

if (!customElements.get('localization-popup')) customElements.define('localization-popup', LocalizationPopup);
